import {
  updateGroupBy,
  updateSortBy,
  maybeUpdateSortByDirection,
  updatePareDown,
  updateFavoritesList,
  updateCountLimit,
  SubheaderSlice,
  updateConfigureSelections,
} from '../Subheader/Subheader.slice';
import { AppThunkDispatch } from 'src/store';
import store from 'src/store';
import { FAVORITE_EXCLUDE_KEYS } from 'src/utils/Domain/Constants';
import {
  FavoriteListItem,
  FavoriteListItemStorage,
  FavoriteResponseItem,
} from 'src/components/Subheader/Favorites/Favorites.types';
import { cloneDeep, groupBy, isArray, isNil, isNumber, omit, xor } from 'lodash';
import { TenantConfigViewData, TenantConfigViewItem } from 'src/dao/tenantConfigClient';
import { coalesce } from 'src/utils/Functions/Coalesce';

// TODO: in the future move all of this into Favorites.utils.tsx

const updateSubheader = (config: FavoriteListItemStorage, subheader: SubheaderSlice, dispatch?: AppThunkDispatch) => {
  if (dispatch) {
    if (isNumber(config.groupBySelection) && config.groupBySelection >= 0) {
      if (config.groupByDataIndexSelection) {
        const selection = subheader.groupBy.options.findIndex((x) => x.dataIndex === config.groupByDataIndexSelection);
        dispatch(updateGroupBy(selection));
      } else {
        dispatch(updateGroupBy(config.groupBySelection));
      }
    }
    if (isNumber(config.sortBySelection) && config.sortBySelection >= 0) {
      if (config.sortByDataIndexSelection) {
        const selection = subheader.sortBy.options.findIndex((x) => x.dataIndex === config.sortByDataIndexSelection);
        dispatch(updateSortBy(selection));
      } else {
        dispatch(updateSortBy(config.sortBySelection));
      }
    }
    if (config.sortByDirection) {
      dispatch(maybeUpdateSortByDirection(config.sortByDirection));
    }
    if (config.pareDownSelections) {
      dispatch(updatePareDown(config.pareDownSelections));
    }
    if (isNumber(config.limitSelection) && config.limitSelection >= 0) {
      dispatch(updateCountLimit(config.limitSelection));
    }
    if (isArray(config.configurationSelections)) {
      dispatch(updateConfigureSelections(config.configurationSelections));
    }
  }
};

export const updateGroupSortViaLocal = (defnId: string | null, dispatch: AppThunkDispatch) => {
  const config = defnId !== null ? localStorage.getItem(defnId) : undefined;
  const subheaderState = store.getState().subheader;
  if (config) {
    const localConfig: FavoriteListItemStorage = JSON.parse(config);
    updateSubheader(localConfig, subheaderState, dispatch);
  }
};

/**
 * Given the current default definition and a favorite list,
 * merge any new props from default into the favorite list, except for:
 *  • visible, hidden, pinned
 *  • dataIndex, text, id
 */
function applyCurrentColumnProps(
  defaultList: TenantConfigViewItem[],
  favList: TenantConfigViewItem[]
): TenantConfigViewItem[] {
   const dupIdx = new Set(
    Object.entries(groupBy(defaultList, 'dataIndex'))
      .filter(([di, items]) => !isNil(di) && items.length > 1)
      .map(([di]) => di as string)
  );
  return favList.map((fav) => {
// If this favorite's dataIndex is duplicated in defaultList, we can't match by dataIndex
    const isDup = !isNil(fav.dataIndex) && dupIdx.has(fav.dataIndex as string);

    // If duplicated: match by text; otherwise: match by dataIndex (then fallback to text)
    const def =
      (isDup
        ? defaultList.find((d) => d.text && fav.text && d.text === fav.text)
        : defaultList.find((d) => d.dataIndex && fav.dataIndex && d.dataIndex === fav.dataIndex) ??
          defaultList.find((d) => d.text && fav.text && d.text === fav.text)
      );

    if (!def) return fav;

    // start with all of default's props…
    const merged: TenantConfigViewItem = { ...def } as any;

    // …but preserve the favorite’s core flags for safety
    if ('dataIndex' in fav) merged.dataIndex = fav.dataIndex;
    if ('id' in fav) merged.id = fav.id;
    if ('visible' in fav) merged.visible = fav.visible;
    if ('hidden' in fav) merged.hidden = fav.hidden;
    if ('pinned' in fav) merged.pinned = fav.pinned;

    // recurse into children if they exist
    if (!isNil(def.columns) && !isNil(fav.columns)) {
      merged.columns = applyCurrentColumnProps(def.columns, fav.columns);
    }

    return merged;
  });
}

/**
 * Take a default list and a favorite list, each keyed by getKey(item),
 * and:
 *  • remove any fav items not in default
 *  • insert any default items not in fav
 *  • preserve default order
 */
function reconcileByKey<T>(
  defaultList: T[],
  favoriteList: T[],
  getKey: (item: T) => string
): T[] {
  // work on a shallow clone so we can splice
  const result = cloneDeep(favoriteList);
  const defaultKeys = defaultList.map(getKey);
  const favoriteKeys = result.map(getKey);

  xor(favoriteKeys, defaultKeys).forEach((key) => {
    const iFav = favoriteKeys.indexOf(key);
    const iDef = defaultKeys.indexOf(key);

    if (iFav > -1 && iDef === -1) {
      // fav has something default no longer does → drop it
      result.splice(iFav, 1);
    } else if (iFav === -1 && iDef > -1) {
      // default has something new → insert it
      result.splice(iDef, 0, defaultList[iDef]);
    }
  });

  return result;
}

export const getFavoriteConfigOriginalKeys = (
  config: TenantConfigViewData,
  unmodifiedViewDefn: TenantConfigViewData,
  getColumns?: () => string[]
): TenantConfigViewData => {
  // If the original viewDefn has the excludeKeys property, strip those out and add the original viewDefns version
  const newConfig = {
    ...unmodifiedViewDefn,
    ...omit(config, FAVORITE_EXCLUDE_KEYS),
  };

  // 2) pick whichever list is active
  const defaultItems = coalesce(unmodifiedViewDefn.view, unmodifiedViewDefn.columns, [])!;
  const favoriteItems = coalesce(newConfig.view, newConfig.columns, [])!;

  // result starts as the mergedConfig, we'll fill in columns or view next
  const resultConfig: TenantConfigViewData = { ...newConfig };

  // 3) Branch: flat columns vs nested view[]
  if (!Array.isArray(unmodifiedViewDefn.view)) {
    // Flat‐columns branch: build defaultList in getColumns order if provided
    const defaultList: TenantConfigViewItem[] = getColumns
      ? getColumns()
        .map((di) => defaultItems.find((c) => c.dataIndex === di))
        .filter((c): c is TenantConfigViewItem => Boolean(c))
      : defaultItems;

    // reconcile by dataIndex
    resultConfig.columns = reconcileByKey(
      defaultList,
      favoriteItems as TenantConfigViewItem[],
      (c) => c.dataIndex
    );
    resultConfig.columns = applyCurrentColumnProps(defaultList, resultConfig.columns);
  } else {
    // Nested‐view branch: diff only at the ROOT level by text, data indicies in favorite are unreliable
    // logic is the same as above just with different key
    const rootsReconciled = reconcileByKey(
      defaultItems as TenantConfigViewItem[],
      favoriteItems as TenantConfigViewItem[],
      (c) => c.text
    );

    // for each matching root with children, reconcile its .columns by dataIndex
    rootsReconciled.forEach((root) => {
      const defRoot = (defaultItems as TenantConfigViewItem[]).find((d) => d.text === root.text);
      if (defRoot?.columns && root.columns) {
        root.columns = reconcileByKey(
          defRoot.columns,
          root.columns,
          (c) => c.dataIndex
        );
      }
    });

    resultConfig.view = rootsReconciled;
    resultConfig.view = applyCurrentColumnProps(
      unmodifiedViewDefn.view,
      resultConfig.view as TenantConfigViewItem[]
    );
  }

  return resultConfig;
};

export const getFavoriteConfigWithoutKeys = (config: TenantConfigViewData): TenantConfigViewData => {
  // If the original viewDefn has the excludeKeys property, strip those out and add the original viewDefns version
  return omit(config, FAVORITE_EXCLUDE_KEYS) as any;
};

export const getLocalConfig = (
  _defnStr: string | null,
  favoritesList: FavoriteResponseItem[],
  dispatch: AppThunkDispatch,
  unmodifiedViewDefn?: TenantConfigViewData,
  getColumns?: () => string[]
) => {
  const state = store.getState();
  const perspective = state.perspective.selected;
  if (!perspective) {
    return;
  }

  dispatch(updateFavoritesList(favoritesList));

  const subheaderState = state.subheader;
  const activeFavorite = favoritesList.find((x) => x.active);

  if (activeFavorite && activeFavorite.jsonBlob) {
    const localConfig: FavoriteListItem = activeFavorite.jsonBlob;

    // In case the favorite has keys that aren't supposed to be there, strip them and add the original configs'

    if (localConfig && localConfig.config && unmodifiedViewDefn) {
      const formattedConfig = getFavoriteConfigOriginalKeys(localConfig.config, unmodifiedViewDefn, getColumns);
      const finalConfig: FavoriteListItem = {
        ...localConfig,
        config: formattedConfig,
      };
      updateSubheader(finalConfig, subheaderState, dispatch);
      return finalConfig;
    } else {
      updateSubheader(localConfig, subheaderState, dispatch);
      return localConfig;
    }
  }
  return;
};
